import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Fournisseur} from '@domain/facture/fournisseur';
import {FournisseursService} from '@services/admin/facture/fournisseurs.service';
import {FournisseursTabsService} from '@services/admin/facture/fournisseurs.tabs.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {ConfirmService} from '@share/component/confirmation/confirm.service';
import {TypeCodeErreur} from '@domain/common/http/result';
import {finalize, first} from 'rxjs/operators';
import {NgForm} from '@angular/forms';
import {Adresse} from "@domain/profil/adresse";
import {LieuParam} from "@domain/common/lieu/lieu-param";
import {LieuParamAdmin} from "@domain/common/lieu/lieu-param-admin";
import {Devise} from "@domain/settings/devise";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {SettingsGlobalState} from "@domain/settings/settings";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {Orga} from "@domain/admin/entreprise/analytique/orga";
import {TypeAction} from "@share/component/floating-button/floating-button";
import {OptionCompte} from "@domain/admin/comptabilite/optionsTransverses";
import {TypeCompteBancaire} from "@domain/comptabilite/compte-bancaire";

/**
 * Composant de gestion des généralités d'un fournisseur
 *
 * @author Yannick Gauthier
 * @date 14/08/2025
 */
@Component({
    host: {'data-test-id': 'factures-fournisseurs-generalites'},
    selector: 'factures-fournisseurs-generalites',
    templateUrl: './factures-fournisseurs-generalites.component.html'
})
export class FacturesFournisseursGeneralitesComponent implements OnInit, OnDestroy {
    /** Initialisation du fournisseur */
    fournisseur: Fournisseur = new Fournisseur({
        idFournisseur: 0,
        isActif: true
    });

    /** Type de compte sélectionné */
    typeCompte: TypeCompte = 'IBAN';

    /** Liste des types de compte */
    listeTypesCompte: Array<{type: TypeCompte, libelle: string}> = [
        {type: 'IBAN', libelle: this.translateService.instant('admin.frais.fournisseurs.infos.iban')},
        {type: 'RIB', libelle: this.translateService.instant('admin.frais.fournisseurs.infos.rib')},
        {type: 'AUTRE', libelle: this.translateService.instant('admin.frais.fournisseurs.infos.autre')}
    ];

    /** Liste des comptes tiers */
    listeComptes: Array<OptionCompte> = [];

    /** Organisme */
    orga: Orga = null;

    /** Liste des devises */
    listeDevises: Devise[];

    /** Localisation de l'adresse */
    localisation: Adresse = new Adresse();

    /** Paramètres de localisation */
    lieuParam: LieuParam;

    /** Flag de création */
    isCreation: boolean = true;

    /** Indicateur d'enregistrement en cours */
    isSaving: boolean = false;

    /** Énumération TypeCompteBancaire pour utilisation dans le template */
    TypeCompteBancaire = TypeCompteBancaire;

    /** Formulaire */
    @ViewChild('fournisseurForm') fournisseurForm: NgForm;

    /**
     * Constructeur
     * @param router Service de routage
     * @param activatedRoute Route activée
     * @param fournisseursService Service de gestion des fournisseurs
     * @param fournisseursTabsService Service de gestion des onglets
     * @param translateService Service de traduction
     * @param toastrService Service de notification
     * @param confirmService Service de confirmation
     * @param store Store de l'application
     */
    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fournisseursService: FournisseursService,
        private fournisseursTabsService: FournisseursTabsService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private confirmService: ConfirmService,
        private store: Store<AppState>
    ) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        this.lieuParam = new LieuParamAdmin();

        //Récupère toutes les devises enregistrées en base
        this.store.select(state => state.settings?.Global).pipe(filterFirstNotNull()).subscribe(
            (settings: SettingsGlobalState) => {
                this.listeDevises = settings.listeDevises;
            });

        //Récupération de l'id du fournisseur dans l'URL
        const idFournisseur = this.activatedRoute.parent.snapshot.params.id;

        //Si l'id est différent de 0, on charge le fournisseur
        if (idFournisseur && idFournisseur !== '0') {
            this.isCreation = false;
        }

        //Chargement du fournisseur
        this.fournisseursService.getFournisseurById(+idFournisseur).pipe(first()).subscribe({
            next: result => {
                //S'il n'y a pas d'erreur
                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                    //Récupération du fournisseur si ce n'est pas une création
                    if (idFournisseur && idFournisseur !== '0') { this.fournisseur = new Fournisseur(result.data.fournisseur); }

                    //Récupération de l'orga
                    this.fournisseur.orga = result.data.orga;

                    //Récupération de la liste des comptes tiers
                    this.listeComptes = result.data.listeCompte;

                    //Créer un nouvel objet Adresse avec les données reçues
                    if (result.data.localisation) {
                        const nouvelleLocalisation = new Adresse();
                        nouvelleLocalisation.idLocalisation = result.data.localisation.id;
                        nouvelleLocalisation.rue = result.data.localisation.entiteGeo.libelle;
                        nouvelleLocalisation.codePostal = result.data.localisation.entiteGeo.codePostal;
                        nouvelleLocalisation.ville = result.data.localisation.entiteGeo.libelleVille;
                        nouvelleLocalisation.pays = result.data.localisation.entiteGeo.libellePays;
                        nouvelleLocalisation.adresse = result.data.localisation.entiteGeo.libelle + ', ' +
                            result.data.localisation.entiteGeo.codePostal + ' ' +
                            result.data.localisation.entiteGeo.libelleVille + ', ' +
                            result.data.localisation.entiteGeo.libellePays;

                        //Assigner le nouvel objet à localisation
                        this.localisation = nouvelleLocalisation;
                    }

                    //Détermination du type de compte
                    if (this.fournisseur?.iban) {
                        this.typeCompte = 'IBAN';
                    } else if (this.fournisseur?.rib) {
                        this.typeCompte = 'RIB';
                    } else {
                        this.typeCompte = 'AUTRE';
                    }

                    //Initialisation des actions
                    this.initActions();
                } else {
                    //Message d'erreur
                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                }
            },
            error: () => {
                //Message d'erreur
                this.toastrService.error(this.translateService.instant('global.errors.chargement'));
            }
        });
    }

    /**
     * Initialisation des actions
     */
    initActions(): void {
        this.fournisseursTabsService.setActions([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                isVisible: () => this.fournisseurForm?.valid,
                doAction: () => this.saveFournisseur()
            },
            {
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                isVisible: () => !this.isCreation && this.fournisseur?.idFournisseur > 0,
                doAction: () => this.deleteFournisseur()
            }
        ]);
    }

    /**
     * Méthode appelée lors de la sauvegarde du fournisseur
     */
    saveFournisseur(): void {
        //Mise à jour de l'indicateur informant que le traitement est en cours
        this.isSaving = true;

        //Nettoyage des champs en fonction du type de compte
        if (this.typeCompte === 'IBAN') {
            this.fournisseur.rib = null;
            this.fournisseur.domiciliation = null;
            this.fournisseur.numeroCompte = null;
        } else if (this.typeCompte === 'RIB') {
            this.fournisseur.iban = null;
            this.fournisseur.bic = null;
            this.fournisseur.numeroCompte = null;
        } else {
            this.fournisseur.iban = null;
            this.fournisseur.bic = null;
            this.fournisseur.rib = null;
        }

        //Assignation de l'id de la localisation
        this.fournisseur.idLocalisation = this.localisation.idLocalisation;

        //Assignation de l'id de l'orga
        if(this.fournisseur.orga?.idOrga) {
            this.fournisseur.idOrga = this.fournisseur.orga.idOrga;
        }

        //Sauvegarde du fournisseur
        this.fournisseursService.saveFournisseur(this.fournisseur)
            .pipe(first(), finalize(() => {
                //Mise à jour l'indicateur informant que le traitement est terminé
                this.isSaving = false;
            }))
            .subscribe({
                next: result => {
                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                        //Mise à jour du fournisseur
                        this.fournisseur = result.data.fournisseur;

                        //Mise à jour de l'indication d'ajout
                        this.isCreation = false;

                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                    }
                },
                error: () => {
                    //Message d'erreur
                    this.toastrService.error(this.translateService.instant('global.erreurs.enregistrement'));
                }
            });
    }

    /**
     * Méthode appelée lors de la suppression du fournisseur
     */
    deleteFournisseur(): void {
        //Mise à jour l'indicateur informant que le traitement est en cours
        this.isSaving = true;

        //Affichage de la confirmation de suppression du fournisseur
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(first())
            .subscribe(isConfirmed => {
                //Si l'utilisateur a confirmé
                if (isConfirmed) {
                    //Suppression du fournisseur
                    this.fournisseursService.deleteFournisseur(this.fournisseur)
                        .pipe(first(), finalize(() => {
                            //Mise à jour l'indicateur informant que le traitement est terminé
                            this.isSaving = false;
                        }))
                        .subscribe({
                            next: result => {
                                //S'il n'y a pas d'erreur
                                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                                    //Message d'information
                                    this.toastrService.success(this.translateService.instant('global.success.suppression'));

                                    //Redirection vers la liste des fournisseurs
                                    this.router.navigate(['Admin/Frais/Factures/Fournisseurs']);
                                } else {
                                    //Message d'erreur
                                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                                }
                            },
                            error: () => {
                                //Message d'erreur
                                this.toastrService.error(this.translateService.instant('global.erreurs.suppression'));
                            }
                        });
                } else {
                    //Mise à jour l'indicateur informant que le traitement est terminé
                    this.isSaving = false;
                }
            });
    }

    /**
     * Hook destruction
     */
    ngOnDestroy(): void {
        //Effacer les actions lors de la destruction du composant
        this.fournisseursTabsService.clearActions();
    }
}

export type TypeCompte = 'IBAN' | 'RIB' | 'AUTRE';
